/**
 * RoadmapCommentInput
 *
 * Text input for posting a roadmap ("Ideas") comment / reply. Structural sibling
 * of the content CommentInput, swapping CommentEntity for RoadmapCommentEntity.
 * Wires `useMentions` to the field so typing `@` opens SearchUsersSheet in
 * mention mode (reused verbatim from the content port).
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InputText, type InputTextElement } from '@/presentation/components/common/InputText';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useMentions } from '@/presentation/hooks/useMentions';
import { SearchUsersSheet } from '@/presentation/feature/content/components/sheets/searchUsers/SearchUsersSheet';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';

export interface RoadmapCommentInputProps {
  replyTo: RoadmapCommentEntity | null;
  onCancelReply: () => void;
  onSend: (text: string) => Promise<boolean>;
  screenKey?: string;
}

export const RoadmapCommentInput: React.FC<RoadmapCommentInputProps> = ({
  replyTo,
  onCancelReply,
  onSend,
  screenKey,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const [value, setValue] = useState('');
  const inputRef = useRef<InputTextElement | null>(null);

  const selectUser = useCallback(
    () => SearchUsersSheet.showForMention(undefined, screenKey),
    [screenKey],
  );
  const { handleChange, isEditingMentions } = useMentions({ selectUser });

  const setValueAndCursor = (text: string, cursorPos: number) => {
    setValue(text);
    // Defer to ensure the value has committed to the DOM before moving cursor
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        try {
          el.setSelectionRange(cursorPos, cursorPos);
        } catch {
          // ignore
        }
      }
    });
  };

  const hasText = value.trim().length > 0;

  const insertMentionSymbol = () => {
    const el = inputRef.current;
    const cursor = el?.selectionStart ?? value.length;
    const next = `${value.slice(0, cursor)}@${value.slice(cursor)}`;
    const nextCursor = cursor + 1;
    const handled = handleChange(next, nextCursor, setValueAndCursor);
    if (!handled) setValue(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(nextCursor, nextCursor); } catch { /* ignore */ }
      // Auto-resize is handled inside InputText (autoResize prop + value effect).
    });
  };

  const send = () => {
    if (!value.trim()) return;
    // Fire-and-forget: the optimistic insert in the parent hook already
    // signals "sent" to the user. Awaiting here would keep the button
    // rotated and the text visible in the input until the Firestore write
    // completes, which feels laggy. Rollback on failure is handled by the
    // parent hook.
    void onSend(value);
    setValue('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {replyTo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: responsive.spacing.lg,
            paddingRight: responsive.spacing.lg,
            paddingTop: responsive.spacing.sm,
            paddingBottom: responsive.spacing.sm,
            backgroundColor: theme.backgroundSecondary,
          }}
        >
          <span
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
            }}
          >
            {l10n.comments.replyingTo.replace('{name}', replyTo.userName)}
          </span>
          <button
            onClick={onCancelReply}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.text.secondary,
              fontSize: responsive.font.sm,
            }}
          >
            {l10n.common.close}
          </button>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
          paddingTop: responsive.spacing.xl,
          paddingBottom: responsive.spacing.xl,
          borderTop: `${responsive.border.thin}px solid ${theme.border}`,
          gap: responsive.gap.sm,
        }}
      >
        <InputText
          inputRef={inputRef}
          value={value}
          onChange={(next) => {
            const cursor = inputRef.current?.selectionStart ?? next.length;
            const handled = handleChange(next, cursor, setValueAndCursor);
            if (!handled) setValue(next);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={l10n.comments.placeholder}
          disabled={isEditingMentions}
          multiline
          autoResize
          lineHeight={1.4}
          maxHeight="6.2em"
          style={{ flex: 1 }}
          qa={{ id: 'roadmap-comment-input' }}
        />
        <button
          onClick={insertMentionSymbol}
          disabled={isEditingMentions}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: isEditingMentions ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            paddingRight: responsive.spacing.sm,
          }}
        >
          <AssetIcon
            path="/assets/images/mention.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={theme.text.primary}
          />
        </button>
        <button
          {...qaAttr({ id: 'roadmap-comment-send' })}
          onClick={send}
          disabled={!value.trim()}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: !value.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: hasText ? 'rotate(-45deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), background-color 0.2s',
          }}
        >
          <AssetIcon
            path="/assets/images/send.png"
            width={responsive.button.size.xxs}
            height={responsive.button.size.xxs}
            color={!hasText ? theme.disabled : theme.exclusive}
            transition="background-color 0.2s"
          />
        </button>
      </div>
    </div>
  );
};
